import { put, select, takeEvery } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';
import { takeLatest } from 'redux-saga/effects';

import { Activity, ActivityController, Proposal } from '@/controllers/Activity';
import { Lead, LeadController } from '@/controllers/Lead';
import { Opportunity, OpportunityController } from '@/controllers/Opportunity';
import { Order, OrderController } from '@/controllers/Order';
import { ROUTES } from '@/router';
import { Navigation } from '@/services/Navigation';
import { Sentry } from '@/services/Sentry';
import { ActivitiesActionTypes } from '@/store/reducers/activities/action-types';
import {
  saveDetailsTargetActivity,
  UpdateDetailsOpportunity,
} from '@/store/reducers/activities/actions';
import {
  selectAppliedFilters,
  selectDetailsTargetActivity,
} from '@/store/reducers/activities/selectors';
import { setGlobalError } from '@/store/reducers/global/actions';
import { TasksActionTypes } from '@/store/reducers/tasks/action-types';
import { CustomErrorsMessages, CustomExceptions, ProposalStatus, ReduxAction } from '@/types';

import { mapOpenTasksResponse } from '../../store/reducers/tasks/helpers/tasks.helper';

import { ActivityInstance } from './types';

function* fetchActivityInstances(activityType: number, activityId: string) {
  let opportunity: Opportunity | null = null;
  let lead: Lead | null = null;
  let order: Order | null = null;

  if (activityType === ActivityInstance.Lead) {
    const response: AxiosResponse<Lead> = yield LeadController.fetchLead(activityId);

    lead = response.data;
  }

  if (activityType === ActivityInstance.Opportunity) {
    const response: AxiosResponse<Opportunity> = yield OpportunityController.fetchOpportunity(
      activityId,
    );

    opportunity = response.data;
  }

  if (
    activityType === ActivityInstance.Order ||
    activityType === ActivityInstance.OrderWithoutProject
  ) {
    type Response = AxiosResponse<Order>;
    const response: Response = yield OrderController.fetchOrder(activityId);

    order = response.data;

    // An order is not last in the change order chain,
    // therefore we change the ID in the URL so user will
    // have the latest order ID from the change order chain
    if (activityId !== order.id) {
      Navigation.goTo(ROUTES.Private.Dashboard.ActivityDetails, {
        urlParams: { detailsTarget: `${ActivityInstance.Order}-${order.id}` },
      });
    }

    if (!order.isDirectOrder && order.opportunityId) {
      const opportunityId = order.opportunityId;
      const result: AxiosResponse<Opportunity> = yield OpportunityController.fetchOpportunity(
        opportunityId,
      );

      opportunity = result.data;

      if (activityType === ActivityInstance.OrderWithoutProject) {
        opportunity.proposals = opportunity.proposals.filter(
          proposal => proposal.status !== ProposalStatus.Archived,
        );
      }
    }
  }

  if (opportunity !== null) {
    opportunity.proposals = opportunity.proposals?.filter(
      proposal => proposal.status !== ProposalStatus.Draft,
    );
  }

  return { order, opportunity, lead };
}

function* fetchOrderHistory(opportunityId: string) {
  let orderHistory: Order[] | null = null;

  try {
    const response: AxiosResponse = yield OrderController.fetchOrderHistory(opportunityId);

    orderHistory = response?.data?.itemList;
  } catch (error) {
    console.error(error);
    yield put(setGlobalError(new Error(CustomErrorsMessages.SomethingWentWrong)));
  }

  return orderHistory;
}

function findLatestProposal(proposals: Proposal[] | undefined): Proposal | null {
  if (!proposals || proposals.length < 1) {
    return null;
  }

  const sortedProposals = proposals.sort((a, b) => {
    const createA = new Date(a.createOn).getTime();
    const createB = new Date(b.createOn).getTime();

    return createB - createA;
  });

  return sortedProposals[0] ?? null;
}

export function* fetchActivityDetailsData(action: ReduxAction) {
  const { activityType, activityId } = action.sagaPayload;
  let orderHistory = null;

  try {
    // if there is no saved activity (in case user access details side-page by link and there is no
    // activity on current pagination page) fetch it and save to store
    const activity: Activity | null = yield select(selectDetailsTargetActivity);

    if (!activity) {
      const response: AxiosResponse<Activity> = yield ActivityController.fetchActivity(activityId);

      yield put(saveDetailsTargetActivity(response.data));
    }

    const { order, opportunity, lead } = yield fetchActivityInstances(
      Number(activityType),
      activityId,
    );

    const { data } = yield ActivityController.fetchTaskStatusesCards(activityId);

    if (Number(activityType) === ActivityInstance.OrderWithoutProject && order?.opportunityId) {
      orderHistory = yield fetchOrderHistory(order.opportunityId);
    }

    const latestProposal = findLatestProposal(opportunity?.proposals);

    yield put({
      type: ActivitiesActionTypes.FETCH_ACTIVITY_DETAILS_DATA.SUCCESS,
      payload: { activityInstances: { lead, opportunity, order } },
    });

    yield put({
      type: ActivitiesActionTypes.SELECT_PROPOSAL,
      payload: latestProposal,
    });

    yield put({
      type: ActivitiesActionTypes.FETCH_ORDERS_HISTORY_DATA.SUCCESS,
      payload: { orderHistory },
    });

    yield put({
      type: ActivitiesActionTypes.FETCH_TASK_CARD_STATUSES.SUCCESS,
      payload: data,
    });
  } catch (error) {
    yield put({ type: ActivitiesActionTypes.FETCH_ACTIVITY_DETAILS_DATA.FAIL, error });

    if (axios.isAxiosError(error)) {
      if (error?.response?.status === 400) {
        yield put(setGlobalError(new Error(CustomExceptions.PageNotFound)));
      } else if (error?.response?.status === 403) {
        Navigation.goTo(ROUTES.Private.Dashboard.ActivityList);
      } else {
        console.error(error);
      }
    }
  }
}

export function* refetchActivities() {
  const appliedFilters = yield select(selectAppliedFilters);
  const activitiesResponse = yield ActivityController.fetchAllActivities(appliedFilters);

  yield put({ type: ActivitiesActionTypes.REMOVE_ACTIVITIES });
  yield put({
    type: ActivitiesActionTypes.FETCH_ACTIVITIES.SUCCESS,
    sagaPayload: { appliedFilters },
    payload: activitiesResponse.data,
  });

  return activitiesResponse.data;
}

function* updateActivityPhotoAlbumLinkHandler(action: ReduxAction) {
  const { activityId, instanceId, photoAlbumLink } = action.payload;

  try {
    switch (instanceId) {
      case ActivityInstance.Lead:
        yield LeadController.updatePhotoAlbumLink(activityId, photoAlbumLink);
        break;

      case ActivityInstance.Opportunity:
        yield OpportunityController.updatePhotoAlbumLink(activityId, photoAlbumLink);
        break;

      case ActivityInstance.Order:
        yield OrderController.updatePhotoAlbumLink(activityId, photoAlbumLink);
        break;
    }

    yield fetchActivityDetailsData({
      type: ActivitiesActionTypes.FETCH_ACTIVITY_DETAILS_DATA.START,
      sagaPayload: { activityType: instanceId, activityId },
    });

    yield put({ type: ActivitiesActionTypes.UPDATE_ACTIVITY_PHOTO_ALBUM_LINK.SUCCESS });
  } catch (error) {
    yield put({ type: ActivitiesActionTypes.UPDATE_ACTIVITY_PHOTO_ALBUM_LINK.FAIL, error });
    console.error(error);
  }
}

function* fetchTaskCardStatuses(action: ReduxAction) {
  try {
    const { data } = yield ActivityController.fetchTaskStatusesCards(action.payload);

    yield put({
      type: ActivitiesActionTypes.FETCH_TASK_CARD_STATUSES.SUCCESS,
      payload: data,
    });
  } catch {
    // Ignore canceled request
  }
}

function* trackProposalUpdate(action: ReduxAction) {
  const opportunityId = action.payload;

  try {
    const response: AxiosResponse = yield OpportunityController.fetchOpportunity(opportunityId);
    const opportunity: Opportunity = response.data;

    yield put(UpdateDetailsOpportunity(opportunity));
  } catch (e) {
    const genericError = new Error(CustomErrorsMessages.SomethingWentWrong);

    Sentry.captureError(e as Error);
    yield put(setGlobalError(genericError));
  }
}

function* fetchOpenTasksCounters() {
  try {
    const { data } = yield ActivityController.getOpenTasksCounters();

    yield put({
      type: TasksActionTypes.FETCH_OPEN_TASKS_COUNTERS.SUCCESS,
      payload: mapOpenTasksResponse(data),
    });
  } catch (error) {
    yield put({ type: TasksActionTypes.FETCH_OPEN_TASKS_COUNTERS.FAIL, error });
    console.error(error);
  }
}

export function* ActivityControllerSaga() {
  yield takeEvery(
    ActivitiesActionTypes.FETCH_ACTIVITY_DETAILS_DATA.START,
    fetchActivityDetailsData,
  );

  yield takeEvery(
    ActivitiesActionTypes.UPDATE_ACTIVITY_PHOTO_ALBUM_LINK.START,
    updateActivityPhotoAlbumLinkHandler,
  );

  yield takeEvery(ActivitiesActionTypes.FETCH_TASK_CARD_STATUSES.START, fetchTaskCardStatuses);

  yield takeLatest(ActivitiesActionTypes.UPDATE_TARGET_ACTIVITY, trackProposalUpdate);

  yield takeEvery(TasksActionTypes.FETCH_OPEN_TASKS_COUNTERS.START, fetchOpenTasksCounters);

  yield takeEvery(ActivitiesActionTypes.REFRESH_ACTIVITIES, refetchActivities);
}
